/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import { FormattedMessage } from 'react-intl';

import HEAttributeFooter from './components/HEAttributeFooter';
import RouteNames from '../../appAuthorized/RouteNames';

import GeneVariantHelpModal from './components/GeneVariantHelpModal';
import MedliNames from '../../../constants/MedliNames';
import { MedliTermListItem } from '../../../apiServices/MedicalTermsService';
import { GeneVariantPresentData } from '../../../models/GeneVariantPresentModel';

import COLORS from '../../styles/Colors';

import ContentFrame from '../../applicationFrame/ContentFrame';
import { ScrollAwareContext, ScrollAwareScroller, ScrollAwareAppNavStep } from '../../components/ScrollAware';
import ContentLoader from '../../components/ContentLoader';

import { AppHeaderScrollableText } from '../../components/appNav/AppHeaderScrollable';
import ContentWrapper from '../../components/ContentWrapper';
import { Card, CardRowUI, Icons } from '../../../bphComponents/bundle';

import { useMedliGeneVariantClassification } from '../../hooks/useMedli';
import { useHealthEntryAndValueForActiveProfile } from '../../hooks/useHealthEntry';
import { useNavigate } from '../../hooks/useNavigate';

import { useUIState } from '../../hooks/useUIState';
import { useLists } from '../../hooks/useLists';

const HealthEntryGeneVariant = (props) => {
  const healthEntryId = Number(props.match.params.healthEntryId);
  const healthEntryValueId = Number(props.match.params.valueId);

  const [, navActions] = useNavigate();
  const [, uiStateActions] = useUIState();
  const [hevState, hevActions] = useHealthEntryAndValueForActiveProfile(healthEntryId, healthEntryValueId);
  const [hasChanges, setHasChanges] = React.useState(false);

  const [{ geneVariantClassificationList }] = useMedliGeneVariantClassification();
  const [{ geneVariantPresentList }] = useLists();
  const sourceHevInstance = hevActions.instantiateHealthEntryValue();

  const pendingHevInstance = hevActions.instantiateEditingHealthEntryValue();

  const isLoaded = hevState.isLoaded;
  const isDeletable = sourceHevInstance.geneVariantResult && Boolean(sourceHevInstance.geneVariantResult.getVariantPresentId());
  const showHelp = () => {
    uiStateActions.showDarkBigBlur(
      <GeneVariantHelpModal />,
      { isWithSliding: true },
    );
  };
  const selectedVariantPresentId = pendingHevInstance.geneVariantResult.getVariantPresentId();
  const selectedClassificationCui = pendingHevInstance.geneVariantResult.getClassificationCui();

  const setGeneVariant = (variantId, medliTerm) => {
    setHasChanges(true);
    const newHev = hevActions.instantiateEditingHealthEntryValue();

    newHev.geneVariantResult.setVariant(variantId);

    const resultMedliTerm = medliTerm || {};
    newHev.geneVariantResult.setClassification(resultMedliTerm.id);
    newHev.geneVariantResult.setClassificationCui(resultMedliTerm.cui);
    hevActions.updateEditing(newHev.data());
  };

  return (
    <div>
      <ScrollAwareContext>
        <ScrollAwareAppNavStep
          submitDisabled={false}
          cancel={() => {
            navActions.goBackTo(RouteNames.healthEntryValue(healthEntryId, healthEntryValueId));
            hevActions.resetEditing();
          }}
          submit={() => {
            if (!hasChanges) {
              navActions.goBackTo(RouteNames.healthEntryValue(healthEntryId, healthEntryValueId));
              return;
            }

            const newHevInstance = hevActions.instantiateEditingHealthEntryValue();
            hevActions.apiUpdate(newHevInstance.data())
              .then(() => {
                navActions.goBackTo(RouteNames.healthEntryValue(healthEntryId, healthEntryValueId));
                hevActions.resetEditing();
              });
          }}
          color="white"
        />

        <ContentLoader isContentLoaded={isLoaded} blockRenderUntilLoaded>
          <ContentFrame>
            <ScrollAwareScroller>
              <div>
                <AppHeaderScrollableText
                  id="healthEntry.geneVariant.title.text"
                />
                <ContentWrapper>

                  <Card.CardShape>
                    {
                      geneVariantPresentList.map((variantPresent: GeneVariantPresentData) => {
                        if (variantPresent.list_type === MedliNames.list.geneVariantClassification) {
                          return <NewSubList
                            key={variantPresent.id}
                            choice={variantPresent}
                            label={`${variantPresent.key}.name`}
                            choices={geneVariantClassificationList}
                            expanded={variantPresent.id === selectedVariantPresentId}
                            subChoiceId={selectedClassificationCui}
                            mainChoiceId={selectedVariantPresentId}
                            onSelect={(medliTerm) => { setGeneVariant(variantPresent.id, medliTerm); }}
                          />;
                        }

                        return (
                          <CardRowUI.SelectableRow
                            key={variantPresent.id}
                            isSelected={variantPresent.id === selectedVariantPresentId}
                            onClick={() => { setGeneVariant(variantPresent.id, null); }}
                            hasBottomBorder={true}
                            selectedClassName="fill-color-profile-highlight"
                          >
                            <div className="font-body font-color-darkest-gray">
                              <FormattedMessage id={`${variantPresent.key}.name`} />
                            </div>
                          </CardRowUI.SelectableRow>
                        );
                      })
                    }
                  </Card.CardShape>

                  <HEAttributeFooter
                    isFlagged={null}
                    onDelete={isDeletable ? () => {
                      const newHevInstance = hevActions.instantiateEditingHealthEntryValue();

                      if (newHevInstance.geneVariantResult) {
                        newHevInstance.geneVariantResult.clear();
                      }
                      hevActions.apiUpdate(newHevInstance.data()).then(() => {
                        navActions.goBack(RouteNames.healthEntryValue(healthEntryId, healthEntryValueId));
                      });
                    } : null}
                    onToggleFlag={null}
                    altLeftContent={(
                      <div className="font-caption-1 font-color-gsMedDark" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }} onClick={showHelp}>
                        <Icons.IconInfo.Large color={COLORS.gsMiddle} />
                        <FormattedMessage id="healthEntry.GeneVariant.help.button.label">
                          {
                            (msg) => <div style={{ display: 'inline-block', verticalAlign: 'inherit' }}>{msg}</div>
                          }
                        </FormattedMessage>
                      </div>
                    )}
                  />
                </ContentWrapper>
              </div>
            </ScrollAwareScroller>
          </ContentFrame>
        </ContentLoader>
      </ScrollAwareContext>
    </div>
  );
};

interface SubListProps {
  label: string;
  choice: GeneVariantPresentData;
  choices: MedliTermListItem[];
  expanded: boolean;
  mainChoiceId: number;
  subChoiceId: string;
  onSelect(choice: MedliTermListItem): void;
}

const NewSubList = (props: SubListProps) => {
  const { label, choice, choices, expanded, mainChoiceId, subChoiceId, onSelect } = props;
  const isSelected = choice.id === mainChoiceId;
  return (
    <div>
      <CardRowUI.SelectableRow
        isSelected={isSelected}
        onClick={() => { onSelect(null); }}
        hasBottomBorder={true}
        selectedClassName="fill-color-profile-highlight"
      >
        <FormattedMessage id={label} />
      </CardRowUI.SelectableRow>

      <Card.CollapsibleContent isExpanded={isSelected}>
        <CardRowUI.Summary
          hasBottomBorder={true}
          backgroundColor={COLORS.gsLightest}
          text={[
            <div key={0} className="font-footnote">
              <FormattedMessage id="healthEntry.GeneVariantClassification.hint" />
            </div>
          ]}
        />

        <Card.CardSection hasBottomBorder={true} style={{ backgroundColor: COLORS.csAppBGgray30 }}>
          {
            choices.map((choice: MedliTermListItem) => (
              <CardRowUI.SelectableRow
                key={choice.cui}
                isSelected={choice.cui === subChoiceId}
                onClick={() => onSelect(choice)}
                hasBottomBorder={true}
                selectedClassName="fill-color-profile-highlight"
              >
                <div className="font-body font-color-gsDark">
                  {choice.string}
                </div>
              </CardRowUI.SelectableRow>
            ))
          }
        </Card.CardSection>
        <div />
      </Card.CollapsibleContent>

    </div>
  );
};

export default HealthEntryGeneVariant;
